// Install guide for shops without per-shop snippet templates (every new self-serve account), in
// Settings → Setup and onboarding step 4.
//
// Self-serve onboarding (spec §5–§6 in Cloudflare-Worker,
// https://github.com/Digital-Mozarts/Cloudflare-Worker, branch feature/self-serve-onboarding):
// the customer can have their landing page checked (a site scan, POST /api/cux/site-scan). The
// guide then says what was detected and why, and its steps, snippet and CSP lines follow the page.
// Until the customer picks where they edit their site, the guide follows the scan; their pick
// always wins. The guide comes from GET /api/cux/install-guide; this component only renders it.
// onEmbedReady hands the snippet to the page so its Test Connection can read the client key, and
// onUrlChange hands it the landing page.
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const HTML_OPTION = 'html';
const FALLBACK_OPTIONS = [{ value: HTML_OPTION, label: 'HTML / server-rendered pages' }];
const LOAD_ERROR = 'Could not load the install guide. Please try again.';
const SCAN_ERROR = "We couldn't check your page just now. Pick where you edit your site below.";

const messageOf = (err, fallback) => (err && err.response && err.response.data && err.response.data.message) || fallback;

// How sure the scan is about the thing that decides the steps: the platform if it found one.
function confidenceOf(detected) {
    const level = detected.platform !== 'none' ? detected.confidence.platform : detected.confidence.framework;
    return level ? `${level} confidence` : '';
}

const InstallGuidePanel = ({ app_url, onEmbedReady, defaultUrl = '', autoScan = false, onUrlChange }) => {
    const [url, setUrl] = useState(defaultUrl);
    const [scan, setScan] = useState(null);
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState('');
    // null until the customer picks: the guide follows the scan (or the general steps).
    const [framework, setFramework] = useState(null);
    const [guide, setGuide] = useState(null);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const autoScanned = useRef(false);
    const scanId = scan ? scan.id : null;

    useEffect(() => {
        let cancelled = false;
        setError('');
        setCopied(false);
        const params = {};
        if (framework) params.framework = framework;
        if (scanId) params.scanId = scanId;
        axios.get(`${app_url}/api/cux/install-guide`, { params, withCredentials: true })
            .then((response) => {
                if (cancelled) return;
                if (response.data && response.data.success) {
                    setGuide(response.data);
                    if (onEmbedReady) onEmbedReady(response.data.embedHtml);
                } else {
                    setGuide(null);
                    setError((response.data && response.data.message) || LOAD_ERROR);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setGuide(null);
                setError(messageOf(err, LOAD_ERROR));
            });
        return () => { cancelled = true; };
        // onEmbedReady is a callback prop; re-fetching when a parent re-creates it would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app_url, framework, scanId]);

    const runScan = async (address) => {
        const target = String(address || '').trim();
        if (!target) return;
        setScanning(true);
        setScanError('');
        try {
            const { data } = await axios.post(`${app_url}/api/cux/site-scan`, { url: target }, { withCredentials: true });
            if (data && data.success && data.scan) {
                // A new scan decides the steps again, until the customer picks.
                setFramework(null);
                setScan(data.scan);
            } else {
                setScanError((data && data.message) || SCAN_ERROR);
            }
        } catch (err) {
            setScanError(messageOf(err, SCAN_ERROR));
        } finally {
            setScanning(false);
        }
    };

    // Once, when the panel opens with an address to check.
    useEffect(() => {
        if (autoScan && !autoScanned.current && String(defaultUrl).trim()) {
            autoScanned.current = true;
            runScan(defaultUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeUrl = (value) => {
        setUrl(value);
        if (onUrlChange) onUrlChange(value);
    };

    const copySnippet = async () => {
        await navigator.clipboard.writeText(guide.embedHtml);
        setCopied(true);
    };

    const options = (guide && guide.frameworks) || FALLBACK_OPTIONS;
    const selected = framework
        || (guide && options.some((option) => option.value === guide.stepsFor) ? guide.stepsFor : HTML_OPTION);
    const detected = guide && guide.detected && !guide.detected.blocked ? guide.detected : null;
    const csp = guide && guide.csp;
    const cspTitle = csp && csp.tailored
        ? (csp.lines.length ? "Your site's Content-Security-Policy needs these changes" : 'Content-Security-Policy')
        : 'If your site sends a Content-Security-Policy';

    return (
        <section className="install-guide" aria-label="Install guide">
            <label htmlFor="install-guide-url" className="charts_filter_label">Your landing page</label>
            <div style={{ display: 'flex', gap: 8 }}>
                <input
                    id="install-guide-url"
                    type="url"
                    className="form-control"
                    placeholder="https://www.yoursite.com/landing-page"
                    value={url}
                    onChange={(e) => changeUrl(e.target.value)}
                />
                <button
                    type="button"
                    className="btn btn-outline-primary"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => runScan(url)}
                    disabled={scanning || !url.trim()}
                >
                    {scanning ? 'Checking…' : 'Check my page'}
                </button>
            </div>
            <p className="text-muted small mt-1 mb-0" aria-live="polite">
                {scanning
                    ? 'Checking your page. This can take up to 30 seconds.'
                    : "We'll look at how your page is built and tell you exactly where the snippet goes."}
            </p>
            {scanError && <p className="text-danger mt-2" aria-live="polite">{scanError}</p>}

            {detected && (
                <div className="install-guide-detected mt-3" aria-label="What we found">
                    <p className="mb-1">
                        <strong>We detected:</strong> <span>{detected.summary}</span>{' '}
                        <span className="text-muted">({confidenceOf(detected)})</span>
                    </p>
                    {detected.evidence.length > 0 && (
                        <details>
                            <summary>How we know</summary>
                            <ul>{detected.evidence.map((line) => <li key={line}>{line}</li>)}</ul>
                        </details>
                    )}
                </div>
            )}
            {guide && guide.findings && guide.findings.map((finding) => (
                <p key={finding.id} className={finding.level === 'warning' ? 'text-danger mt-2 mb-0' : 'text-muted mt-2 mb-0'}>
                    {finding.text}
                </p>
            ))}

            <label htmlFor="install-guide-framework" className="charts_filter_label mt-3">Where do you edit your site?</label>
            <select
                id="install-guide-framework"
                className="form-select"
                value={selected}
                onChange={(e) => setFramework(e.target.value)}
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>

            {error && <p role="alert" className="text-danger mt-2">{error}</p>}

            {guide && (
                <>
                    <p className="mt-3 mb-1">Paste this at the very top of your page's <code>&lt;head&gt;</code>:</p>
                    <pre aria-label="Snippet" className="install-guide-snippet"><code>{guide.embedHtml}</code></pre>
                    <button type="button" className="btn btn-primary btn-sm" onClick={copySnippet}>
                        {copied ? 'Copied' : 'Copy snippet'}
                    </button>

                    <ol className="mt-3" aria-label="Steps">
                        {guide.steps.map((step) => <li key={step}>{step}</li>)}
                    </ol>

                    <details className="mt-2" open={!!(csp.tailored && csp.lines.length)}>
                        <summary>{cspTitle}</summary>
                        {csp.lines.length > 0 && (
                            <ul aria-label="CSP sources">
                                {csp.lines.map((line) => <li key={line}><code>{line}</code></li>)}
                            </ul>
                        )}
                        {csp.notes.map((note) => <p key={note} className="mb-1">{note}</p>)}
                    </details>
                </>
            )}
        </section>
    );
};

export default InstallGuidePanel;
